import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  Building2,
  CreditCard,
  LayoutDashboard,
  MessageSquareText,
  ShieldCheck,
  Wallet,
  Workflow,
} from "lucide-react";
import SEO from "../components/SEO";
import NotFound from "./NotFound";
import { getProductBySlug } from "../data/products";

const detailContent = {
  spacem8: {
    strapline:
      "Property management with more visibility, less friction, and better control.",
    overview:
      "Space-m8 helps property teams run buildings, tenants, operations, and service coordination in one connected environment. It is designed for owners, managers, and operational teams who need daily visibility without depending on scattered spreadsheets, message threads, and disconnected tools.",
    mainSellPoints: [
      "One platform for spaces, tenants, collections, requests, and day-to-day oversight",
      "Clearer operational visibility for managers and property teams",
      "Faster response loops around tenant needs, maintenance, and payments",
      "AI intelligence that helps surface patterns, guide follow-up, and support smarter property decisions",
      "A modular structure that can grow with your portfolio and rollout priorities",
    ],
    modules: [
      {
        name: "Portfolio and Space Management",
        description:
          "Track properties, units, occupancy, and availability from one operational dashboard.",
      },
      {
        name: "Tenant and Relationship Records",
        description:
          "Keep resident information, leasing details, and communication history organized and accessible.",
      },
      {
        name: "Billing and Collections Support",
        description:
          "Monitor payment activity, expected collections, and follow-up workflows with better visibility.",
      },
      {
        name: "Maintenance and Issue Resolution",
        description:
          "Capture requests, assign action, monitor response time, and reduce operational blind spots.",
      },
    ],
    benefits: [
      "Less time chasing information across disconnected channels",
      "Stronger visibility into occupancy, collections, and operational workload",
      "Cleaner collaboration between property managers, finance teams, and support staff",
      "A more professional digital experience for both internal teams and tenants",
    ],
    flow: [
      "Property or unit is onboarded into the portfolio structure",
      "Tenant records, occupancy details, and billing information are connected",
      "Operations team monitors requests, collections, and day-to-day tasks in one workspace",
      "Managers use dashboards and reports to guide follow-up, service quality, and growth decisions",
    ],
    pricing: [
      "Spacem8 is available through modular rollout planning rather than one rigid package.",
      "Pricing can be shaped around the size of the portfolio, the modules you need, and the level of onboarding or support required.",
      "Payment plans can be discussed for phased implementation, annual commitments, or structured rollout schedules.",
    ],
    contactMessage:
      "I am interested in Space-m8. Please share modules, pricing options, payment plans, and the best setup path for our property portfolio.",
    metrics: [
      { label: "Occupancy visibility", value: 88, color: "bg-cyan-400" },
      { label: "Collections tracking", value: 76, color: "bg-yellow-400" },
      { label: "Issue response monitoring", value: 69, color: "bg-emerald-400" },
    ],
  },
  impactbase: {
    strapline:
      "A purpose-built NGO operating system that keeps programs, procurement, HR, and compliance aligned.",
    overview:
      "ImpactBase helps non-governmental organisations manage projects, procurement, human resources, and internal controls in one integrated environment. It is designed for organisations that need operational discipline, stronger accountability, and better visibility without forcing teams to work across disconnected systems.",
    mainSellPoints: [
      "One integrated platform for projects, procurement, HR, and oversight",
      "Embedded NGO-specific compliance controls instead of manual policy policing",
      "An AI Assistant that helps teams retrieve answers, understand workflow status, and move faster",
      "An Intelligence Report function that turns operational data into decision-ready insight",
      "Stronger transparency for donor-facing and internal accountability",
      "Better coordination across program, operations, finance, and approval teams",
      "A tamper-resistant audit trail that supports trust and governance",
    ],
    modules: [
      {
        name: "Project and Grant Management",
        description:
          "Track projects, timelines, activities, and budget-linked delivery in one shared system.",
      },
      {
        name: "Procurement and Vendor Governance",
        description:
          "Manage requisitions, bidding, approvals, and vendor due diligence with policy-aware workflows.",
      },
      {
        name: "Human Resources and Team Records",
        description:
          "Organize staff data, approvals, and operational workflows that support internal coordination.",
      },
      {
        name: "Compliance, Controls, and Audit Trail",
        description:
          "Enforce donor rules, approval layers, and traceable decision history without manual patchwork.",
      },
      {
        name: "AI Assistant and Intelligence Report",
        description:
          "Support teams with AI-guided answers, operational summaries, and report views that surface patterns, exceptions, and performance signals.",
      },
    ],
    benefits: [
      "Reduced operational fragmentation across core NGO functions",
      "More reliable enforcement of donor budget and procurement policies",
      "Faster access to answers and operational context through the AI Assistant",
      "Stronger leadership visibility through Intelligence Report summaries",
      "Clearer accountability for approvals, vendor decisions, and project execution",
      "Better reporting readiness through cleaner data and traceable workflows",
    ],
    flow: [
      "Program team initiates a project activity, request, or budget-linked need",
      "Procurement or HR workflow routes through the right approval and compliance checks",
      "AI Assistant helps users interpret status, policy context, and next best actions",
      "Vendor, budget, and policy controls are validated before action proceeds",
      "Leaders and operations teams use Intelligence Reports to monitor progress, exceptions, and readiness from one source of truth",
    ],
    pricing: [
      "ImpactBase can be scoped around organisation size, number of users, and the combination of modules required.",
      "Pricing discussions can include phased deployment, implementation support, user onboarding, and change management.",
      "Payment plans can be structured for NGO budgeting cycles, staged rollout milestones, or broader institutional agreements.",
    ],
    contactMessage:
      "I am interested in ImpactBase. Please share modules, pricing options, payment plans, and the best setup path for our NGO operations.",
    metrics: [
      { label: "Approval traceability", value: 91, color: "bg-cyan-400" },
      { label: "Policy enforcement readiness", value: 83, color: "bg-yellow-400" },
      { label: "Operational visibility", value: 78, color: "bg-emerald-400" },
      { label: "AI-assisted decision support", value: 74, color: "bg-fuchsia-400" },
    ],
  },
  "vertex-locomotives": {
    strapline:
      "A mobility commerce platform connecting vehicle sales, garage services, and safari experiences in one digital ecosystem.",
    overview:
      "Vertex Locomotives is designed to bring together automotive inventory, service workflows, customer engagement, and safari offerings under one operational brand. It supports businesses that want a stronger digital presence across vehicle sales, after-sales service, and experience-based offerings.",
    mainSellPoints: [
      "One platform for vehicle sales, garage operations, and safari-related customer journeys",
      "Clearer digital visibility for inventory, service requests, and customer interaction",
      "A stronger automotive commerce experience for both operators and buyers",
      "Space to grow from listings and inquiries into a broader mobility ecosystem",
      "A more premium and organized digital front for multi-service vehicle businesses",
    ],
    modules: [
      {
        name: "Vehicle Listings and Sales Management",
        description:
          "Showcase available vehicles, pricing, and inquiry pathways in a cleaner buyer-facing experience.",
      },
      {
        name: "Garage and Service Booking",
        description:
          "Support service inquiries, repair coordination, and customer follow-up across garage operations.",
      },
      {
        name: "Customer Relationship and Inquiry Handling",
        description:
          "Keep buyer and client engagement organized from first interest to follow-up and conversion.",
      },
      {
        name: "Safari Offerings and Experience Layer",
        description:
          "Present safari-related packages or offerings as part of a broader lifestyle and mobility brand story.",
      },
    ],
    benefits: [
      "A more unified digital presence across sales, service, and safari offerings",
      "Better visibility into vehicle demand, customer interest, and service follow-up",
      "Cleaner customer journeys from inquiry to response and conversion",
      "A stronger foundation for scaling an automotive and mobility brand online",
    ],
    flow: [
      "Vehicle inventory or service offering is published to the platform",
      "Customers browse listings, ask questions, or request service and safari information",
      "The business team tracks responses, bookings, and buyer interest in one place",
      "Operators monitor visibility, inquiries, and service activity to improve conversion and experience",
    ],
    pricing: [
      "Vertex Locomotives can be discussed based on the modules required across vehicle sales, service operations, and safari offerings.",
      "Pricing can include staged rollout, custom branding, onboarding support, and operational setup guidance.",
      "Payment plans can be structured around implementation phases, business readiness, and broader platform scope.",
    ],
    contactMessage:
      "I am interested in Vertex Locomotives. Please share modules, pricing options, payment plans, and the best setup path for our automotive and service business.",
    metrics: [
      { label: "Inventory visibility", value: 84, color: "bg-cyan-400" },
      { label: "Service booking readiness", value: 71, color: "bg-yellow-400" },
      { label: "Customer engagement tracking", value: 77, color: "bg-emerald-400" },
    ],
  },
  "tibagrid-hms": {
    strapline:
      "Connected healthcare intelligence for modern clinics and hospitals.",
    overview:
      "TibaGrid HMS is built for clinics and hospitals that want to digitize care delivery, reduce operational delays, and create a more intelligent healthcare environment. It connects patient records, appointments, consultations, lab and pharmacy workflows, billing, and claims into one coordinated system designed for real-world healthcare operations.",
    mainSellPoints: [
      "One connected hospital operating system for clinical, administrative, and financial workflows",
      "Digitized patient records that improve continuity, visibility, and speed of care",
      "Appointment, consultation, lab, pharmacy, and billing journeys connected in one platform",
      "Insurance and SHA claims support that reduces paperwork friction and turnaround delays",
      "AI-powered intelligence that surfaces real-time operational insight, trends, and workflow opportunities",
      "A modular healthcare ecosystem that can fit both full hospitals and lighter clinic environments",
    ],
    modules: [
      {
        name: "TibaGrid HMS",
        description:
          "The main hospital management system that connects core patient, clinical, operational, and financial workflows in one environment.",
      },
      {
        name: "TibaGrid Clinic",
        description:
          "A lightweight version for clinics that need fast digitization of appointments, records, consultations, and day-to-day front-desk operations.",
      },
      {
        name: "TibaGrid Lab",
        description:
          "Laboratory workflow management for test intake, result tracking, reporting, and better turnaround visibility.",
      },
      {
        name: "TibaGrid Pharmacy",
        description:
          "Pharmacy and stock control support for prescriptions, dispensing, inventory visibility, and supply coordination.",
      },
      {
        name: "TibaGrid Claims",
        description:
          "Insurance and SHA claims workflow support to organize submissions, follow-up, verification, and financial tracking.",
      },
      {
        name: "TibaGrid IQ",
        description:
          "The AI analytics and automation layer that turns hospital operations into real-time insight, alerts, summaries, and smarter decision support.",
      },
    ],
    benefits: [
      "Cleaner patient journeys from booking to consultation, lab work, pharmacy, and payment",
      "Reduced administrative friction through digitized records and connected workflows",
      "Stronger visibility for hospital leadership into service volume, bottlenecks, and financial flow",
      "Improved coordination across clinicians, reception, lab teams, pharmacy staff, and finance teams",
      "A scalable digital foundation for both multi-department hospitals and smaller clinics",
    ],
    flow: [
      "Patient is registered and their medical record is created or retrieved digitally",
      "Appointments, triage, and consultations are coordinated through the shared care workflow",
      "Lab requests, prescriptions, and internal referrals move through connected modules",
      "Billing, claims, and payment activity are tracked with better operational visibility",
      "TibaGrid IQ highlights trends, delays, service pressure points, and real-time intelligence for management teams",
    ],
    pricing: [
      "TibaGrid HMS can be deployed as a full hospital platform or scoped into lighter modules depending on operational readiness and facility size.",
      "Pricing can be shaped around the modules required, number of departments or branches, user volume, onboarding depth, and support expectations.",
      "Payment plans can be discussed for phased implementation, pilot rollouts, annual commitments, or wider healthcare digitization programs.",
    ],
    contactMessage:
      "I am interested in TibaGrid HMS. Please share modules, pricing options, payment plans, and the best setup path for our clinic or hospital.",
    metrics: [
      { label: "Patient flow visibility", value: 86, color: "bg-cyan-400" },
      { label: "Claims workflow readiness", value: 73, color: "bg-yellow-400" },
      { label: "Lab and pharmacy coordination", value: 81, color: "bg-emerald-400" },
      { label: "AI intelligence coverage", value: 77, color: "bg-fuchsia-400" },
    ],
  },
};

function Spacem8Mockups() {
  return (
    <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
      <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-5 shadow-[0_0_24px_rgba(15,23,42,0.28)]">
        <div className="rounded-[1.4rem] border border-white/10 bg-[linear-gradient(180deg,#101a25,#0a0f15)] p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80">
                Sample Dashboard
              </p>
              <h3 className="mt-2 text-2xl font-bold text-white">
                Portfolio command view
              </h3>
            </div>
            <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">
              Live view
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {[
              ["Occupied units", "184"],
              ["Expected collections", "KES 4.8M"],
              ["Open requests", "27"],
            ].map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-white/8 bg-black/25 px-4 py-4"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                  {label}
                </p>
                <p className="mt-2 text-2xl font-bold text-white">{value}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-[0.7fr_1.3fr]">
            <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                Task board
              </p>
              <div className="mt-4 space-y-3">
                {[
                  "3 maintenance visits scheduled",
                  "11 follow-ups due for collections",
                  "2 new vacancies ready for listing",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-xl border border-white/8 bg-white/[0.04] px-3 py-3 text-sm text-slate-200"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                Monthly performance snapshot
              </p>
              <div className="mt-5 flex h-40 items-end gap-3">
                {[
                  ["Jan", "h-20"],
                  ["Feb", "h-24"],
                  ["Mar", "h-28"],
                  ["Apr", "h-24"],
                  ["May", "h-32"],
                  ["Jun", "h-36"],
                ].map(([month, height], index) => (
                  <div key={month} className="flex flex-1 flex-col items-center gap-2">
                    <div
                      className={`w-full rounded-t-2xl ${height} ${
                        index % 2 === 0 ? "bg-yellow-400/80" : "bg-cyan-400/80"
                      }`}
                    />
                    <span className="text-xs text-slate-400">{month}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6">
        <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-5 shadow-[0_0_24px_rgba(15,23,42,0.28)]">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80">
            Tenant experience
          </p>
          <div className="mt-4 rounded-[1.4rem] border border-white/8 bg-black/20 p-4">
            <div className="flex items-center justify-between">
              <p className="text-lg font-semibold text-white">Tenant App Preview</p>
              <div className="rounded-full bg-emerald-400/10 px-3 py-1 text-xs text-emerald-300">
                Active
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {[
                "View rent status and payment history",
                "Raise maintenance requests in a few taps",
                "See follow-up updates from the property team",
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-xl border border-white/8 bg-white/[0.04] px-3 py-3 text-sm text-slate-200"
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-5 shadow-[0_0_24px_rgba(15,23,42,0.28)]">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80">
            Sample flow
          </p>
          <div className="mt-4 space-y-3">
            {[
              "Property setup",
              "Tenant onboarding",
              "Billing and collections tracking",
              "Maintenance handling and reporting",
            ].map((step, index) => (
              <div key={step} className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.04] px-4 py-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-400/15 text-sm font-bold text-yellow-300">
                  {index + 1}
                </div>
                <p className="text-sm text-slate-200">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ImpactBaseMockups() {
  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
      <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-5 shadow-[0_0_24px_rgba(15,23,42,0.28)]">
        <div className="rounded-[1.4rem] border border-white/10 bg-[linear-gradient(180deg,#101a25,#0a0f15)] p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80">
                NGO Operations Hub
              </p>
              <h3 className="mt-2 text-2xl font-bold text-white">
                Programs, procurement, and controls in one view
              </h3>
            </div>
            <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">
              Compliance ready
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[
              ["Active projects", "34"],
              ["Pending approvals", "19"],
              ["Verified vendors", "86"],
              ["AI assistant prompts", "142"],
            ].map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-white/8 bg-black/25 px-4 py-4"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                  {label}
                </p>
                <p className="mt-2 text-2xl font-bold text-white">{value}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-[0.8fr_1.2fr]">
            <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                Alerts and exceptions
              </p>
              <div className="mt-4 space-y-3">
                {[
                  "2 requests exceed donor category threshold",
                  "5 approvals waiting at finance review stage",
                  "1 vendor profile needs renewed due diligence",
                  "AI Assistant suggested 3 report-ready summaries",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-xl border border-white/8 bg-white/[0.04] px-3 py-3 text-sm text-slate-200"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                Approval pipeline
              </p>
              <div className="mt-5 space-y-3">
                {[
                  ["Program initiation", "12 items", "bg-cyan-400/80"],
                  ["Procurement review", "7 items", "bg-yellow-400/80"],
                  ["Finance approval", "5 items", "bg-emerald-400/80"],
                  ["Intelligence review", "4 items", "bg-fuchsia-400/80"],
                ].map(([step, count, color]) => (
                  <div key={step}>
                    <div className="mb-2 flex items-center justify-between text-sm text-slate-300">
                      <span>{step}</span>
                      <span>{count}</span>
                    </div>
                    <div className="h-3 rounded-full bg-white/10">
                      <div className={`h-3 rounded-full ${color}`} style={{ width: "75%" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6">
        <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-5 shadow-[0_0_24px_rgba(15,23,42,0.28)]">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80">
            Governance snapshot
          </p>
          <div className="mt-4 space-y-3">
            {[
              "Budget checks applied before approvals proceed",
              "Competitive bidding rules enforced inside workflow steps",
              "Decision history retained in a tamper-resistant log",
              "AI Assistant helps users retrieve answers and workflow context quickly",
              "Intelligence Reports summarize risks, bottlenecks, and performance signals",
            ].map((item) => (
              <div
                key={item}
                className="rounded-xl border border-white/8 bg-white/[0.04] px-4 py-4 text-sm text-slate-200"
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-5 shadow-[0_0_24px_rgba(15,23,42,0.28)]">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80">
            Sample operating flow
          </p>
          <div className="mt-4 space-y-3">
            {[
              "Project activity or request is created",
              "Budget and policy checks are applied",
              "Approval chain routes to the right reviewers",
              "Action is logged and visible for reporting",
            ].map((step, index) => (
              <div key={step} className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.04] px-4 py-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-400/15 text-sm font-bold text-yellow-300">
                  {index + 1}
                </div>
                <p className="text-sm text-slate-200">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function VertexMockups() {
  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
      <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-5 shadow-[0_0_24px_rgba(15,23,42,0.28)]">
        <div className="rounded-[1.4rem] border border-white/10 bg-[linear-gradient(180deg,#101a25,#0a0f15)] p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80">
                Mobility commerce hub
              </p>
              <h3 className="mt-2 text-2xl font-bold text-white">
                Vehicle sales, service, and safari interest in one view
              </h3>
            </div>
            <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">
              Customer active
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {[
              ["Live listings", "48"],
              ["Service requests", "21"],
              ["Safari inquiries", "9"],
            ].map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-white/8 bg-black/25 px-4 py-4"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                  {label}
                </p>
                <p className="mt-2 text-2xl font-bold text-white">{value}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-[0.8fr_1.2fr]">
            <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                Buyer and service alerts
              </p>
              <div className="mt-4 space-y-3">
                {[
                  "4 buyers requested call-backs on SUV inventory",
                  "6 vehicles due for featured listing refresh",
                  "3 service jobs waiting estimate approval",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-xl border border-white/8 bg-white/[0.04] px-3 py-3 text-sm text-slate-200"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                Inquiry mix
              </p>
              <div className="mt-5 space-y-3">
                {[
                  ["Vehicle inquiries", "63%", "bg-cyan-400/80"],
                  ["Garage services", "24%", "bg-yellow-400/80"],
                  ["Safari offerings", "13%", "bg-emerald-400/80"],
                ].map(([step, count, color]) => (
                  <div key={step}>
                    <div className="mb-2 flex items-center justify-between text-sm text-slate-300">
                      <span>{step}</span>
                      <span>{count}</span>
                    </div>
                    <div className="h-3 rounded-full bg-white/10">
                      <div className={`h-3 rounded-full ${color}`} style={{ width: count }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6">
        <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-5 shadow-[0_0_24px_rgba(15,23,42,0.28)]">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80">
            Customer journey
          </p>
          <div className="mt-4 space-y-3">
            {[
              "Browse inventory and compare available vehicles",
              "Request service or garage support",
              "Ask about safari packages and related experiences",
            ].map((item) => (
              <div
                key={item}
                className="rounded-xl border border-white/8 bg-white/[0.04] px-4 py-4 text-sm text-slate-200"
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-5 shadow-[0_0_24px_rgba(15,23,42,0.28)]">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80">
            Sample operating flow
          </p>
          <div className="mt-4 space-y-3">
            {[
              "Inventory and services are published",
              "Customers browse, inquire, or book",
              "Business team tracks leads, responses, and bookings",
              "Operators monitor conversion and service activity",
            ].map((step, index) => (
              <div key={step} className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.04] px-4 py-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-400/15 text-sm font-bold text-yellow-300">
                  {index + 1}
                </div>
                <p className="text-sm text-slate-200">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TibaGridMockups() {
  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
      <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-5 shadow-[0_0_24px_rgba(15,23,42,0.28)]">
        <div className="rounded-[1.4rem] border border-white/10 bg-[linear-gradient(180deg,#101a25,#0a0f15)] p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80">
                Healthcare command center
              </p>
              <h3 className="mt-2 text-2xl font-bold text-white">
                Patient flow, clinical work, and claims in one view
              </h3>
            </div>
            <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">
              Care active
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[
              ["Patients today", "164"],
              ["Pending lab results", "23"],
              ["Claims in review", "18"],
              ["AI alerts", "11"],
            ].map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-white/8 bg-black/25 px-4 py-4"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                  {label}
                </p>
                <p className="mt-2 text-2xl font-bold text-white">{value}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-[0.85fr_1.15fr]">
            <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                Operational alerts
              </p>
              <div className="mt-4 space-y-3">
                {[
                  "7 patients waiting consultation beyond target time",
                  "3 lab results ready for clinician review",
                  "5 prescriptions need pharmacy fulfillment",
                  "TibaGrid IQ flagged a claims bottleneck in outpatient billing",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-xl border border-white/8 bg-white/[0.04] px-3 py-3 text-sm text-slate-200"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                Department flow mix
              </p>
              <div className="mt-5 space-y-3">
                {[
                  ["Consultations", "68%", "bg-cyan-400/80"],
                  ["Lab workflows", "49%", "bg-yellow-400/80"],
                  ["Pharmacy fulfilment", "57%", "bg-emerald-400/80"],
                  ["Claims processing", "42%", "bg-fuchsia-400/80"],
                ].map(([step, count, color]) => (
                  <div key={step}>
                    <div className="mb-2 flex items-center justify-between text-sm text-slate-300">
                      <span>{step}</span>
                      <span>{count}</span>
                    </div>
                    <div className="h-3 rounded-full bg-white/10">
                      <div className={`h-3 rounded-full ${color}`} style={{ width: count }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6">
        <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-5 shadow-[0_0_24px_rgba(15,23,42,0.28)]">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80">
            Ecosystem view
          </p>
          <div className="mt-4 space-y-3">
            {[
              "TibaGrid Clinic manages front-desk, booking, and everyday patient movement",
              "TibaGrid Lab keeps diagnostics organized from test request to result release",
              "TibaGrid Pharmacy supports stock control, dispensing, and prescription flow",
              "TibaGrid Claims helps finance teams track insurance and SHA claim movement",
              "TibaGrid IQ gives leadership real-time intelligence and summaries",
            ].map((item) => (
              <div
                key={item}
                className="rounded-xl border border-white/8 bg-white/[0.04] px-4 py-4 text-sm text-slate-200"
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-5 shadow-[0_0_24px_rgba(15,23,42,0.28)]">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80">
            Sample operating flow
          </p>
          <div className="mt-4 space-y-3">
            {[
              "Patient registration and digital record retrieval",
              "Consultation, lab request, and prescription routing",
              "Billing and claims submission tracking",
              "Leadership monitoring through TibaGrid IQ insights",
            ].map((step, index) => (
              <div key={step} className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.04] px-4 py-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-400/15 text-sm font-bold text-yellow-300">
                  {index + 1}
                </div>
                <p className="text-sm text-slate-200">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProductBySlug(slug);

  if (!product) {
    return <NotFound />;
  }

  const isSpacem8 = slug === "spacem8";
  const isImpactBase = slug === "impactbase";
  const isVertex = slug === "vertex-locomotives";
  const isTibaGrid = product.slug === "tibagrid-hms";
  const detail = detailContent[product.slug];

  return (
    <>
      <SEO
        title={`${product.title} | Jisedai Products`}
        description={product.description}
        url={`https://www.jisedai.africa/products/${product.slug}`}
      />

      <main className="relative overflow-hidden bg-black px-6 pb-24 pt-32 text-white">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/4 top-16 h-72 w-72 rounded-full bg-yellow-400/10 blur-3xl" />
          <div className="absolute bottom-10 right-1/4 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl">
          <Link
            to="/#projects"
            className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300 transition hover:text-cyan-200"
          >
            <ArrowLeft size={16} />
            Back to products
          </Link>

          <div className="mt-8 grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div>
              <div className="inline-flex rounded-full border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-sm font-medium text-cyan-300">
                {product.category}
              </div>
              {product.icon ? (
                <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[linear-gradient(180deg,#f8fafc,#eef7f7)] p-2">
                    <img
                      src={product.icon}
                      alt={`${product.title} icon`}
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-300/80">
                      Product identity
                    </p>
                    <p className="mt-1 text-sm text-slate-300">
                      Healthcare intelligence by JisedAI
                    </p>
                  </div>
                </div>
              ) : null}
              <h1 className="mt-6 max-w-4xl text-4xl font-black leading-tight text-yellow-400 md:text-6xl">
                {product.title}
              </h1>
              <p className="mt-6 max-w-3xl text-xl leading-9 text-slate-200">
                {detail?.strapline || product.description}
              </p>
              <p className="mt-6 max-w-3xl leading-8 text-slate-300">
                {detail?.overview || product.description}
              </p>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/#contact"
                  state={{
                    productMessage:
                      detail?.contactMessage ||
                      `I am interested in ${product.title}. Please share pricing, modules, and the best next steps.`,
                    serviceValue: "AI Consulting",
                  }}
                  className="inline-flex items-center justify-center rounded-2xl bg-yellow-400 px-8 py-4 font-semibold text-black transition hover:bg-yellow-300"
                >
                  Request a demo
                </Link>
                <Link
                  to="/#contact"
                  state={{
                    productMessage:
                      detail?.contactMessage ||
                      `I would like to discuss ${product.title} pricing and rollout options.`,
                    serviceValue: "AI Consulting",
                  }}
                  className="inline-flex items-center justify-center rounded-2xl border border-yellow-400/35 px-8 py-4 font-semibold text-yellow-300 transition hover:bg-yellow-400 hover:text-black"
                >
                  Talk pricing and payment plans
                </Link>
              </div>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-[#0c1118] p-8 shadow-[0_0_30px_rgba(15,23,42,0.32)]">
              <div
                className={`flex min-h-[22rem] items-center justify-center rounded-[1.6rem] border p-8 ${
                  product.logoSurface === "light"
                    ? "border-slate-200/80 bg-[linear-gradient(180deg,#f8fafc,#eef7f7)]"
                    : "border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))]"
                }`}
              >
                <img
                  src={product.logo}
                  alt={product.logoAlt}
                  className={`w-auto object-contain ${
                    product.logoClassName ? "max-w-full" : "max-h-56"
                  } ${product.logoClassName || ""}`}
                />
              </div>
            </div>
          </div>

          {isSpacem8 || isImpactBase || isVertex || isTibaGrid ? (
            <>
              <section className="mt-16 rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-8">
                <div className="flex items-center gap-3 text-yellow-300">
                  <MessageSquareText />
                  <h2 className="text-3xl font-bold text-white">Main sell points</h2>
                </div>
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  {detail.mainSellPoints.map((item) => (
                    <div
                      key={item}
                      className="rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-4 leading-7 text-slate-200"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </section>

              <section className="mt-20">
                <div className="mb-8">
                  <p className="text-sm font-semibold uppercase tracking-[0.26em] text-cyan-300/80">
                    Inside the app
                  </p>
                  <h2 className="mt-3 text-3xl font-bold text-white md:text-4xl">
                    Sample views, signals, and workflow shape
                  </h2>
                </div>
                {isSpacem8 ? (
                  <Spacem8Mockups />
                ) : isImpactBase ? (
                  <ImpactBaseMockups />
                ) : isVertex ? (
                  <VertexMockups />
                ) : (
                  <TibaGridMockups />
                )}
              </section>

              <section className="mt-20 grid gap-8 lg:grid-cols-3">
                <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-8">
                  <div className="flex items-center gap-3 text-yellow-300">
                    <Building2 />
                    <h3 className="text-2xl font-bold text-white">
                      Core features
                    </h3>
                  </div>
                  <div className="mt-6 space-y-4">
                    {detail.modules.map((module) => (
                      <div key={module.name} className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                        <p className="font-semibold text-white">{module.name}</p>
                        <p className="mt-2 text-sm leading-6 text-slate-300">
                          {module.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-8">
                  <div className="flex items-center gap-3 text-yellow-300">
                    <BarChart3 />
                    <h3 className="text-2xl font-bold text-white">
                      Sample performance view
                    </h3>
                  </div>
                  <div className="mt-6 space-y-5">
                    {detail.metrics.map((metric) => (
                      <div key={metric.label}>
                        <div className="mb-2 flex items-center justify-between text-sm text-slate-300">
                          <span>{metric.label}</span>
                          <span>{metric.value}%</span>
                        </div>
                        <div className="h-3 rounded-full bg-white/10">
                          <div
                            className={`h-3 rounded-full ${metric.color}`}
                            style={{ width: `${metric.value}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 rounded-2xl border border-white/8 bg-white/[0.03] p-4 text-sm leading-6 text-slate-300">
                    Sample metrics like these help teams understand where
                    workflow pressure, service performance, and operational
                    follow-through need attention.
                  </div>
                </div>

                <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-8">
                  <div className="flex items-center gap-3 text-yellow-300">
                    <Workflow />
                    <h3 className="text-2xl font-bold text-white">
                      Benefits and outcomes
                    </h3>
                  </div>
                  <div className="mt-6 space-y-3">
                    {detail.benefits.map((benefit) => (
                      <div
                        key={benefit}
                        className="rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-4 text-sm leading-6 text-slate-200"
                      >
                        {benefit}
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="mt-20 grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
                <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-8">
                  <div className="flex items-center gap-3 text-yellow-300">
                    <LayoutDashboard />
                    <h3 className="text-2xl font-bold text-white">
                      Sample app flow
                    </h3>
                  </div>
                  <div className="mt-6 space-y-4">
                    {detail.flow.map((item, index) => (
                      <div
                        key={item}
                        className="flex items-start gap-4 rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-4"
                      >
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-yellow-400/15 text-sm font-bold text-yellow-300">
                          {index + 1}
                        </div>
                        <p className="leading-7 text-slate-200">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-8">
                  <div className="flex items-center gap-3 text-yellow-300">
                    <Wallet />
                    <h3 className="text-2xl font-bold text-white">
                      Pricing, modules, and payment plans
                    </h3>
                  </div>
                  <div className="mt-6 space-y-4">
                    {detail.pricing.map((item) => (
                      <div
                        key={item}
                        className="rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-4 leading-7 text-slate-200"
                      >
                        {item}
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 grid gap-4 md:grid-cols-3">
                    {[
                      {
                        title: "Starter rollout",
                        icon: CreditCard,
                        text: "Begin with the most essential modules and expand later.",
                      },
                      {
                        title: "Phased plan",
                        icon: Workflow,
                        text: "Structure payments around staged implementation milestones.",
                      },
                      {
                        title: "Enterprise setup",
                        icon: ShieldCheck,
                        text: "Plan around a larger portfolio, deeper support, or custom needs.",
                      },
                    ].map((card) => {
                      const Icon = card.icon;
                      return (
                        <div
                          key={card.title}
                          className="rounded-2xl border border-white/8 bg-black/20 p-4"
                        >
                          <Icon className="text-yellow-300" />
                          <p className="mt-4 font-semibold text-white">{card.title}</p>
                          <p className="mt-2 text-sm leading-6 text-slate-300">
                            {card.text}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                    <Link
                      to="/#contact"
                      state={{
                        productMessage: detail.contactMessage,
                        serviceValue: "AI Consulting",
                      }}
                      className="inline-flex items-center justify-center rounded-2xl bg-yellow-400 px-6 py-4 font-semibold text-black transition hover:bg-yellow-300"
                    >
                      Contact us about payment plans
                    </Link>
                    <a
                      href={`mailto:info@jisedai.africa?subject=${encodeURIComponent(
                        `${product.title} Modules and Pricing`
                      )}`}
                      className="inline-flex items-center justify-center rounded-2xl border border-white/10 px-6 py-4 font-semibold text-white transition hover:border-yellow-400/30 hover:text-yellow-200"
                    >
                      Email for module options
                    </a>
                  </div>
                </div>
              </section>
            </>
          ) : (
            <section className="mt-20 rounded-[1.8rem] border border-white/10 bg-[#0c1218] p-8">
              <div className="flex items-center gap-3 text-yellow-300">
                <MessageSquareText />
                <h2 className="text-3xl font-bold text-white">Product brief</h2>
              </div>
              <p className="mt-6 max-w-4xl leading-8 text-slate-300">
                {product.description}
              </p>
              <div className="mt-8 grid gap-4 md:grid-cols-3">
                {product.highlights.map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-4 text-sm leading-6 text-slate-200"
                  >
                    {item}
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link
                  to="/#contact"
                  state={{
                    productMessage: `I am interested in ${product.title}. Please share product details, pricing, modules, and the next steps.`,
                    serviceValue: "AI Consulting",
                  }}
                  className="inline-flex items-center justify-center rounded-2xl bg-yellow-400 px-8 py-4 font-semibold text-black transition hover:bg-yellow-300"
                >
                  Contact us about this product
                </Link>
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}
